import { pluralizeWord } from '@/utils/helpers'

import CertificationRequestQ from '@/queries/CertificationRequestQ'
import { SAT_BOOTCAMP_ONBOARDING_TOPIC, TopicType } from '@/queries/constants'
import ProgramConsentQ from '@/queries/ProgramConsentQ'
import SATUserQ from '@/queries/SATUserQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTagQ from '@/queries/SeriesTagQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import TutorAuditQ from '@/queries/TutorAuditQ'
import TutorProfileQ from '@/queries/TutorProfileQ'
import UserQ from '@/queries/UserQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { SQL } from '@/client/query'
import { PROGRAM_SLUG_TO_PROGRAM_DETAILS } from '@/client/services/programs/config'
import { PROGRAM_WORLD_TO_PROGRAM_SLUG } from '@/client/services/programs/constants'
import composeRichText from '@/client/services/richText/composeRichText'
import { getOnboardingSessionEligibilityStatus } from '@/client/services/satBootcamp/helpers'
import { TutorAuditorLevel } from '@/client/services/sessionAudit'
import { type SeriesTag, SeriesTag as SeriesTagType } from '@/client/services/tags/types'
import {
  ADJUST_AS_NEEDED_TRAINING_SLUG,
  CAW_COLLEGE_LIST_TRAINING_SLUG,
  CAW_THE_APPLICATION_TRAINING_SLUG,
  CAW_THE_ESSAY_TRAINING_SLUG,
  DEVELOP_CONNECTIONS_TRAINING_SLUG,
  ENGAGE_ACTIVELY_TRAINING_SLUG,
  LAY_THE_FOUNDATION_TRAINING_SLUG,
  SAFETY_TRAINING_SLUG,
  SAT_BOOTCAMP_ONBOARDING_TRAINING_SLUG,
} from '@/client/services/training'
import { getArticleUrl } from '@/client/services/url/faq'

import {
  AddPhoneNumberCTA,
  BecomeTutorElsewhereCTA,
  CAWTutorConsentCTA,
  CoachApplyCTA,
  CollegeVerifyCTA,
  CompleteSATBootcampOnboardingTrainingCTA,
  GetCertifiedCTA,
  HostOfferingCTA,
  HostSATBootcampOnboardingSessionCTA,
  LearningLabsTutorConsentCTA,
  PeerReviewSessionsCTA,
} from '@/components/Worlds/Journey/CTAs'

import {
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  type SAT_BOOTCAMP_WORLD_SLUG,
  SAT_PREP_WORLD_SLUG,
} from '../../constants'
import type { ProgramSubworldSlug } from '../../types'
import { LevelRequirementStatus } from '../enum'
import type { LevelRequirement } from '../types'

// By default, if subjects don't have a certification option, we don't allow users to tutor them. For example, Schoolhouse Hub or Schoolhouse Product. If we have alternative ways to get ceritfied (such as for College Admissions Workshops), we  override the world requirements in getWorldRequirementsOverride.
export const INVITE_ONLY_SUBJECT_TODO: LevelRequirement = {
  key: 'manual-approval-required',
  type: 'todo',
  title: 'This subject is invite-only. Tutors are selected by the Schoolhouse team.',
  getLevelStatusQueryFunction: () => SQL`${LevelRequirementStatus.Incomplete}`,
  Component: () => null,
}

export const BECOME_TUTOR_ELSEWHERE_TODO: LevelRequirement = {
  key: 'become-tutor-elsewhere',
  type: 'todo',
  title: 'Become a tutor in another subject or program',
  getLevelStatusQueryFunction: ({ userId }) =>
    SQL`(
        CASE WHEN EXISTS(SELECT 1 ${WorldMemberQ.where(
          { userId },
          WorldMemberQ.isAtLeastLevel('tutor')
        )})
        THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
    END)`,
  Component: BecomeTutorElsewhereCTA,
  estimatedTimeMinutes: 5,
}

export const ADD_PHONE_NUMBER_TODO: LevelRequirement = {
  key: 'add-phone-number',
  type: 'todo',
  title: 'Add your phone number',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN
        (${UserQ.phone_number} IS NOT NULL
            OR ${UserQ.phone_number_waived_at} IS NOT NULL
            OR EXISTS(SELECT 1 ${SATUserQ.where(
              { userId, type: 'tutor' },
              SQL`${SATUserQ.phone_number_waived_at} IS NOT NULL`
            )})
        )
        THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${UserQ.where({ id: userId })}
  `,
  Component: AddPhoneNumberCTA,
}

export const BECOME_INTERMEDIATE_PEER_REVIEWER_TODO: LevelRequirement = {
  key: 'become-intermediate-peer-reviewer',
  type: 'todo',
  title: 'Become an intermediate-level peer reviewer',
  alwaysUnlocked: true,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN
        EXISTS(SELECT 1 ${TutorProfileQ.where(
          { userId },
          TutorProfileQ.hasAtLeastAuditorLevel(TutorAuditorLevel.Intermediate)
        )})
        THEN ${LevelRequirementStatus.Complete}
        WHEN EXISTS(SELECT 1 ${TutorAuditQ.where({ auditorId: userId })})
        THEN ${LevelRequirementStatus.InProgress}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${UserQ.where({ id: userId })}
  `,
  Component: PeerReviewSessionsCTA,
  InProgressComponent: PeerReviewSessionsCTA,
}

export const createBecomeFullyCertifiedInCourseTodo = (courseName?: string): LevelRequirement => ({
  key: 'become-fully-certified-in-course',
  type: 'todo',
  title: `Become fully certified in ${courseName ?? 'one course'}`,
  getLevelStatusQueryFunction: ({ worldSlug, userId }) => SQL`
  (
   CASE WHEN (
        COUNT(CASE WHEN ${TopicQ.hasAnyType([TopicType.challenge, TopicType.ap])}
          AND ${CertificationRequestQ.id} IS NOT NULL THEN 1 END) > 0
        OR COUNT(CASE WHEN ${TopicQ.hasType(TopicType.unit)} AND ${CertificationRequestQ.id} IS NOT NULL THEN 1 END)
          >= ${
            // For SAT prep, getting certified in either math or r&w is sufficient
            // TODO: Generalize this once we have a better sense of how this should work.
            worldSlug === SAT_PREP_WORLD_SLUG
              ? 1
              : SQL`COUNT(CASE WHEN ${TopicQ.hasType(TopicType.unit)} THEN 1 END)`
          }
      )
       THEN ${LevelRequirementStatus.Complete}
       WHEN COUNT(DISTINCT ${CertificationRequestQ.id}) > 0
       THEN ${LevelRequirementStatus.InProgress}
       ELSE ${LevelRequirementStatus.Incomplete}
       END
   )
   ${TopicQ.where(
     {
       active: true,
       courseId: SQL`(SELECT ${WorldQ.course_id} ${WorldQ.where({
         slug: worldSlug,
       })})`,
       include: [
         {
           association: 'CertificationRequests',
           required: false,
           where: SQL`(${CertificationRequestQ.user_id} = ${userId} AND ${CertificationRequestQ.approved_at} IS NOT NULL)`,
         },
       ],
     },
     TopicQ.hasAnyType([TopicType.challenge, TopicType.unit, TopicType.ap])
   )}
 `,
  Component: GetCertifiedCTA,
  InProgressComponent: GetCertifiedCTA,
})

const HIGHLY_ENGAGING_SESSION_MIN_THRESHOLD = 10

export const HOST_TEN_HIGHLY_ENGAGING_SESSIONS_TODO: LevelRequirement = {
  key: 'host-five-highly-engaging-sessions',
  type: 'todo',
  title: composeRichText(ctx =>
    ctx.paragraph(
      ctx.text('Host ten '),
      ctx.linkExternal(getArticleUrl('highly-engaging-session'), 'highly engaging sessions')
    )
  ),
  alwaysUnlocked: true,
  getNumCompletedQueryFunction: ({ userId, worldSlug }) => SQL`
    (SELECT COUNT(*) ${SessionQ.getHighlyEngagingSessions({
      userId,
      worldSlug,
    })})
  `,
  getLevelStatusQueryFunction: ({ worldSlug, userId }) => SQL`
    (SELECT
        CASE WHEN COUNT(*) >= ${HIGHLY_ENGAGING_SESSION_MIN_THRESHOLD} THEN ${
          LevelRequirementStatus.Complete
        }
          WHEN COUNT(*) > 0 THEN ${LevelRequirementStatus.InProgress}
          ELSE ${LevelRequirementStatus.Incomplete}
        END
      ${SessionQ.getHighlyEngagingSessions({ userId, worldSlug })}
    )
 `,
}

const MIN_SESSIONS_HOSTED_THRESHOLD = 14

// Since CAW has some offerings with only 4 sessions, we want to enforce an additional senior tutor requirement of a minimum number of sessions hosted
export const HOST_MIN_SESSIONS_CAW_TODO: LevelRequirement = {
  key: 'host-min-sessions',
  type: 'todo',
  title: composeRichText(ctx =>
    ctx.paragraph(ctx.text(`Host at least ${MIN_SESSIONS_HOSTED_THRESHOLD} sessions`))
  ),
  alwaysUnlocked: true,
  getNumCompletedQueryFunction: ({ userId, worldSlug }) => SQL`
    (SELECT COUNT(*) ${SessionQ.getHostedSessions({ userId, worldSlug })})
  `,
  getLevelStatusQueryFunction: ({ userId, worldSlug }) => SQL`
    (SELECT
        CASE WHEN COUNT(*) >= ${MIN_SESSIONS_HOSTED_THRESHOLD} THEN ${
          LevelRequirementStatus.Complete
        }
          WHEN COUNT(*) > 0 THEN ${LevelRequirementStatus.InProgress}
          ELSE ${LevelRequirementStatus.Incomplete}
        END
        ${SessionQ.getHostedSessions({ userId, worldSlug })}
    )
 `,
  Component: HostOfferingCTA,
  InProgressComponent: HostOfferingCTA,
}

export const HOST_SAT_BOOTCAMP_ONBOARDING_SESSION_TODO: LevelRequirement = {
  key: 'host-sat-bootcamp-onboarding-session',
  type: 'todo',
  title: 'Host an SAT Bootcamp onboarding session',
  getLevelStatusQueryFunction: ({ userId }) => getOnboardingSessionEligibilityStatus({ userId }),
  explanation: {
    [LevelRequirementStatus.Locked]: 'Please host a highly engaging bootcamp first!',
  },
  Component: HostSATBootcampOnboardingSessionCTA,
}

export const COMPLETE_SAT_BOOTCAMP_ONBOARDING_TRAINING_TODO: LevelRequirement = {
  key: 'sat-bootcamp-onboarding-training',
  type: 'todo',
  title: "Complete 'How to lead an SAT Bootcamp Onboarding Session' training",
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN
        ${UserQ.hasCompletedTraining({
          slug: SAT_BOOTCAMP_ONBOARDING_TRAINING_SLUG,
          userId,
        })}
        OR ${SessionHostQ.hasTutoredSessionWithTopicAndAtLeastOneLearner({
          userId,
          topicName: SAT_BOOTCAMP_ONBOARDING_TOPIC,
        })}
        THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
  `,
  Component: CompleteSATBootcampOnboardingTrainingCTA,
}

export const HOST_HIGH_QUALITY_SAT_BOOTCAMP_TODO: LevelRequirement = {
  key: 'host-high-quality-sat-bootcamp',
  type: 'todo',
  title: composeRichText(ctx =>
    ctx.paragraph(
      ctx.text('Host a '),
      ctx.linkExternal(getArticleUrl('sat-bootcamp-senior-tutor'), 'highly engaging SAT Bootcamp')
    )
  ),
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN
        EXISTS(SELECT 1 ${SeriesTagQ.where({
          tag: SeriesTagType.HighlyEngagingSATBootcamp,
          include: [
            {
              association: 'Series',
              required: true,
              where: SQL`${SeriesQ.user_id} = ${userId}`,
            },
          ],
        })})
        OR EXISTS(SELECT 1 ${SATUserQ.where(
          { userId },
          SATUserQ.hasEngagingBootcampRequirementWaived()
        )})
        THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
  `,
  Component: HostOfferingCTA,
}

export const HOST_TWO_HIGHLY_ENGAGING_CAWS_TODO: LevelRequirement = {
  key: 'host-high-quality-caw',
  type: 'todo',
  title: 'Host two college admissions workshops',
  // highly engaging CAW = attended 7/8 sessions
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (SELECT (CASE WHEN
        COUNT(*) >= 2
          THEN ${LevelRequirementStatus.Complete}
        WHEN COUNT(*) > 0
          THEN ${LevelRequirementStatus.InProgress}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${SeriesTagQ.where({
      tag: SeriesTagType.HighlyEngagingCollegeAdmissionsWorkshop,
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`${SeriesQ.user_id} = ${userId}`,
        },
      ],
    })})
  `,
  Component: HostOfferingCTA,
  alwaysUnlocked: true,
}

export const HOST_HIGHLY_ENGAGING_PROGRAMS_TODO = ({
  worldSlug,
  numSeriesRequired,
  seriesTag,
}: {
  worldSlug: Exclude<ProgramSubworldSlug, typeof SAT_BOOTCAMP_WORLD_SLUG>
  numSeriesRequired: number
  seriesTag: SeriesTag
}): LevelRequirement => {
  const programSlug = PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug]
  const programDetails = PROGRAM_SLUG_TO_PROGRAM_DETAILS[programSlug]
  const { programType } = programDetails

  return {
    key: `host-high-quality-${programSlug}-program`,
    type: 'todo',
    title: composeRichText(ctx =>
      ctx.paragraph(
        ctx.text('Host '),
        ctx.text(numSeriesRequired === 1 ? 'a' : `${numSeriesRequired}`),
        ctx.text(' highly engaging '),
        ctx.text(numSeriesRequired > 1 ? pluralizeWord(programType) : programType)
      )
    ),
    getLevelStatusQueryFunction: ({ userId }) => SQL`
    (SELECT (CASE WHEN
        COUNT(*) >= ${numSeriesRequired}
          THEN ${LevelRequirementStatus.Complete}
        WHEN COUNT(*) > 0
          THEN ${LevelRequirementStatus.InProgress}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${SeriesTagQ.where({
      tag: seriesTag,
      include: [
        {
          association: 'Series',
          required: true,
          where: SQL`${SeriesQ.user_id} = ${userId}`,
        },
      ],
    })})
  `,
    Component: HostOfferingCTA,
    alwaysUnlocked: true,
  }
}

export const COMPLETE_THREE_PEER_REVIEWS_TODO: LevelRequirement = {
  key: 'complete-3-peer-reviews',
  type: 'todo',
  title: 'Complete 3 peer reviews in this subworld',
  getLevelStatusQueryFunction: ({ userId, worldSlug }) => SQL`
    (
      SELECT (
        CASE WHEN COUNT(*) >= 3
          THEN ${LevelRequirementStatus.Complete}
        WHEN COUNT(*) > 0
          THEN ${LevelRequirementStatus.InProgress}
        ELSE ${LevelRequirementStatus.Incomplete}
        END
      )
      ${TutorAuditQ.getPeerReviewsInWorld({ userId, worldSlug })}
    )
  `,
  Component: PeerReviewSessionsCTA,
  alwaysUnlocked: true,
}

export const APPLY_COACH_TODO: LevelRequirement = {
  key: 'apply-coach',
  type: 'todo',
  title:
    'Build an outstanding track record as a Senior Tutor, and submit an application when recruitment opens',
  getLevelStatusQueryFunction: ({ userId, worldSlug }) => SQL`
    (
      CASE WHEN
        EXISTS(SELECT 1 ${WorldMemberQ.where({
          userId,
          level: 'coach',
          include: [
            {
              association: 'World',
              required: true,
              where: SQL`${WorldQ.slug} = ${worldSlug}`,
            },
          ],
        })})
        THEN ${LevelRequirementStatus.Complete}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
  `,
  Component: CoachApplyCTA,
}

export const CAW_TUTOR_CONSENT_TODO: LevelRequirement = {
  key: 'caw-tutor-consent',
  type: 'todo',
  title: 'Complete the consent form',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
  (
    CASE WHEN EXISTS(SELECT 1 ${ProgramConsentQ.where({
      userId: UserQ.id,
      type: 'tutor',
      include: [
        {
          association: 'World',
          required: true,
          where: SQL`${WorldQ.slug} = ${COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG}`,
        },
      ],
    })})
      THEN ${LevelRequirementStatus.Complete}
      ELSE ${LevelRequirementStatus.Incomplete}
    END
  )
  ${UserQ.where({ id: userId })}`,
  Component: CAWTutorConsentCTA,
}

export const LEARNING_LABS_TUTOR_CONSENT_TODO: LevelRequirement = {
  key: 'learning-labs-tutor-consent',
  type: 'todo',
  title: 'Complete the consent form',
  estimatedTimeMinutes: 1,
  getLevelStatusQueryFunction: ({ userId, worldSlug }) => SQL`
  (
    CASE WHEN EXISTS(SELECT 1 ${ProgramConsentQ.where({
      userId: UserQ.id,
      type: 'tutor',
      include: [
        {
          association: 'World',
          required: true,
          where: SQL`${WorldQ.slug} = ${worldSlug}`,
        },
      ],
    })})
      THEN ${LevelRequirementStatus.Complete}
      ELSE ${LevelRequirementStatus.Incomplete}
    END
  )
  ${UserQ.where({ id: userId })}`,
  Component: LearningLabsTutorConsentCTA,
}

export const COLLEGE_VERIFY_TODO: LevelRequirement = {
  key: 'verify-college',
  type: 'todo',
  title: 'Verify your affiliation with a college',
  estimatedTimeMinutes: 2,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN ${UserQ.college_info_verified_at} IS NOT NULL
        THEN ${LevelRequirementStatus.Complete}
        WHEN ${UserQ.college_info} IS NOT NULL THEN ${LevelRequirementStatus.InProgress}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${UserQ.where({ id: userId })}
  `,
  Component: CollegeVerifyCTA,
}

export const COLLEGE_VERIFY_USA_TODO: LevelRequirement = {
  key: 'verify-college',
  type: 'todo',
  title:
    'Verify your admission, enrollment, or graduation from a 4-year, United States-based college or university',
  estimatedTimeMinutes: 2,
  getLevelStatusQueryFunction: ({ userId }) => SQL`
    (
      CASE WHEN ${UserQ.college_info_verified_at} IS NOT NULL
            AND ${UserQ.hasCollegeInfoCountryCode('US')}
        THEN ${LevelRequirementStatus.Complete}
        WHEN ${
          UserQ.college_info_verified_at
        } IS NOT NULL AND NOT ${UserQ.hasCollegeInfoCountryCode('US')}
        THEN ${LevelRequirementStatus.Locked}
        WHEN ${UserQ.college_info} IS NOT NULL AND ${UserQ.college_info_verified_at} IS NULL THEN ${
          LevelRequirementStatus.InProgress
        }
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
    ${UserQ.where({ id: userId })}
  `,
  Component: CollegeVerifyCTA,
  explanation: {
    [LevelRequirementStatus.Locked]:
      'You must be enrolled in a US-based college to tutor College Admissions Workshops',
  },
}

const LEADS_TRAINING_REQUIREMENTS: (LevelRequirement & {
  type: 'complete-training'
})[] = [
  {
    key: 'lay-the-foundation-training',
    type: 'complete-training',
    slug: LAY_THE_FOUNDATION_TRAINING_SLUG,
    title: 'Lay the Foundation',
  },
  {
    key: 'engage-actively-training',
    type: 'complete-training',
    slug: ENGAGE_ACTIVELY_TRAINING_SLUG,
    title: 'Engage Actively',
  },
  {
    key: 'adjust-as-needed-training',
    type: 'complete-training',
    slug: ADJUST_AS_NEEDED_TRAINING_SLUG,
    title: 'Adjust as Needed',
  },
  {
    key: 'develop-connections-training',
    type: 'complete-training',
    slug: DEVELOP_CONNECTIONS_TRAINING_SLUG,
    title: 'Develop Connections',
  },
  {
    key: 'safety-training',
    type: 'complete-training',
    slug: SAFETY_TRAINING_SLUG,
    title: 'Safety',
  },
]

// Add strong typing to be able to use this in LEADSTrainingModal
export const LEADS_TRAINING_REQUIREMENT_GROUP: LevelRequirement & {
  type: 'requirement-group'
  requirements: (LevelRequirement & { type: 'complete-training' })[]
} = {
  key: 'leads-training',
  type: 'requirement-group',
  requirements: LEADS_TRAINING_REQUIREMENTS,
  title: 'Complete LEAD tutor trainings',
  ctaText: 'Complete Trainings',
  completeCTAText: 'Review Trainings',
  modalTitle: 'LEAD Tutor Trainings',
  modalDescription:
    'The LEAD trainings will equip you with the core skills needed to become an effective Schoolhouse tutor.',
  doNotBlockWhenInProgress: false,
  requireAll: true,
}

const CAW_CURRICULUM_TRAINING_REQUIREMENTS: LevelRequirement[] = [
  {
    key: 'caw-essay-training',
    type: 'complete-training',
    slug: CAW_THE_ESSAY_TRAINING_SLUG,
    title: 'College Admissions Workshop: The Essay',
    alwaysUnlocked: true,
  },
  {
    key: 'caw-application-training',
    type: 'complete-training',
    slug: CAW_THE_APPLICATION_TRAINING_SLUG,
    title: 'College Admissions Workshop: The Application',
    alwaysUnlocked: true,
  },
  {
    key: 'caw-college-list-training',
    type: 'complete-training',
    slug: CAW_COLLEGE_LIST_TRAINING_SLUG,
    title: 'College Admissions Workshop: How to Create Your College List',
    alwaysUnlocked: true,
  },
]

export const CAW_CURRICULUM_TRAINING_REQUIREMENT_GROUP: LevelRequirement = {
  key: 'caw-curriculum-training',
  type: 'requirement-group',
  requirements: CAW_CURRICULUM_TRAINING_REQUIREMENTS,
  title: 'Complete a Curriculum Training',
  ctaText: 'Complete a Training',
  completeCTAText: 'Review Trainings',
  modalTitle: 'Curriculum Trainings',
  modalDescription:
    'These curriculum trainings will equip you with the core skills needed to become an effective Schoolhouse tutor.',
  doNotBlockWhenInProgress: false,
  requireAll: false,
}
